<template>
  <header class="mall-head" :class="{ 'mall-head--stuck': stuck }">
    <!-- 본 사이트로 돌아가는 통로 -->
    <div class="mall-head__util">
      <div class="mall-head__util-inner">
        <router-link to="/" class="mall-head__back">
          <AppIcon icon="mdi-chevron-left" :size="16" />
          <span class="mall-head__back-full">장기요양지원센터</span>
          <span class="mall-head__back-short">본사이트</span>
        </router-link>

        <div class="mall-head__util-links">
          <template v-if="isLoggedIn">
            <div ref="notifyRef" class="mall-head__notify">
              <button
                type="button"
                class="mall-head__util-link mall-head__notify-btn"
                :aria-expanded="notifyOpen"
                aria-label="알림"
                @click.stop="toggleNotify"
              >
                <AppIcon icon="mdi-bell-outline" :size="16" />
                <em v-if="unreadCount" class="mall-head__notify-dot">{{ badgeText(unreadCount) }}</em>
              </button>
              <div v-if="notifyOpen" class="mall-head__notify-panel">
                <p class="mall-head__notify-title">알림</p>
                <button
                  v-for="item in notifies"
                  :key="item.idx"
                  type="button"
                  class="mall-head__notify-item"
                  :class="{ 'is-unread': !item.read }"
                  @click="openNotify(item)"
                >
                  <strong>{{ item.title }}</strong>
                  <span v-if="item.body">{{ item.body }}</span>
                </button>
                <p v-if="!notifies.length" class="mall-head__notify-empty">새 알림이 없습니다.</p>
              </div>
            </div>

            <router-link
              v-if="managePageTo"
              :to="managePageTo"
              class="mall-head__util-link mall-head__util-link--keep"
            >
              관리자페이지
            </router-link>

            <div
              ref="accountRef"
              class="mall-head__account"
              @mouseenter="openAccount"
              @mouseleave="closeAccountSoon"
            >
              <button
                type="button"
                class="mall-head__welcome"
                :class="{ 'is-open': accountOpen }"
                :aria-expanded="accountOpen"
                :title="personName"
                aria-haspopup="true"
                @click.stop="toggleAccount"
              >
                <span>{{ welcomeName }}님 환영합니다</span>
                <AppIcon icon="mdi-chevron-down" :size="14" />
              </button>

              <div v-if="accountOpen" class="mall-head__account-panel">
                <p class="mall-head__account-name">{{ personName }}</p>
                <router-link :to="MALL_MYPAGE_PATH" @click="closeAccount">마이페이지</router-link>
                <router-link :to="MALL_ORDERS_PATH" @click="closeAccount">주문내역</router-link>
                <router-link :to="MALL_SHIPPING_PATH" @click="closeAccount">배송지</router-link>
                <button type="button" class="mall-head__account-logout" @click="onLogout">
                  로그아웃
                </button>
              </div>
            </div>
          </template>
          <template v-else-if="managePageTo">
            <router-link :to="managePageTo" class="mall-head__util-link mall-head__util-link--keep">
              관리자페이지
            </router-link>
            <button type="button" class="mall-head__util-link mall-head__util-link--keep" @click="onStaffLogout">
              로그아웃
            </button>
          </template>
          <template v-else>
            <button
              ref="loginButtonRef"
              type="button"
              class="mall-head__util-link mall-head__util-link--keep"
              aria-haspopup="dialog"
              :aria-expanded="loginOpen"
              @click="openLogin"
            >
              로그인
            </button>
            <router-link to="/mall/signup" class="mall-head__util-link mall-head__util-link--keep">
              회원가입
            </router-link>
          </template>
        </div>
      </div>
    </div>

    <div class="mall-head__main">
      <div class="mall-head__main-inner">
        <div class="mall-head__brand-row">
          <router-link :to="MALL_HOME_PATH" class="mall-head__brand">
            <AppIcon icon="mdi-cart-percent" :size="26" />
            <span class="mall-head__brand-name">{{ MALL_NAME }}</span>
          </router-link>
          <div class="mall-head__phone-tools">
            <router-link
              :to="MALL_WISHLIST_PATH"
              class="mall-head__phone-icon"
              :class="{ 'is-active': route.path.startsWith(MALL_WISHLIST_PATH) }"
              aria-label="찜"
            >
              <AppIcon icon="mdi-heart-outline" :size="22" />
              <span v-if="wishCount" class="mall-head__phone-badge">{{ badgeText(wishCount) }}</span>
            </router-link>
            <router-link
              :to="MALL_CART_PATH"
              class="mall-head__phone-icon"
              :class="{ 'is-active': route.path.startsWith(MALL_CART_PATH) }"
              aria-label="장바구니"
            >
              <AppIcon icon="mdi-cart-outline" :size="22" />
              <span v-if="productCount" class="mall-head__phone-badge">{{ badgeText(productCount) }}</span>
            </router-link>
            <div v-if="phoneAccountName" ref="phoneAccountRef" class="mall-head__phone-account">
              <button
                type="button"
                class="mall-head__phone-icon"
                :class="{ 'is-open': accountOpen, 'is-active': myInfoActive }"
                :aria-expanded="accountOpen"
                :title="`${phoneAccountName}님`"
                aria-label="내정보"
                aria-haspopup="true"
                @click.stop="toggleAccount"
              >
                <AppIcon icon="mdi-account-outline" :size="22" />
                <span>{{ phoneAccountName }}</span>
              </button>
              <div v-if="accountOpen" class="mall-head__account-panel">
                <p class="mall-head__account-name">{{ phoneAccountTitle }}</p>
                <template v-if="isLoggedIn">
                  <router-link :to="MALL_MYPAGE_PATH" @click="closeAccount">마이페이지</router-link>
                  <router-link :to="MALL_ORDERS_PATH" @click="closeAccount">주문내역</router-link>
                  <router-link :to="MALL_SHIPPING_PATH" @click="closeAccount">배송지</router-link>
                  <button type="button" class="mall-head__account-logout" @click="onLogout">
                    로그아웃
                  </button>
                </template>
                <template v-else>
                  <router-link v-if="managePageTo" :to="managePageTo" @click="closeAccount">
                    관리자페이지
                  </router-link>
                  <button type="button" class="mall-head__account-logout" @click="onStaffLogout">
                    로그아웃
                  </button>
                </template>
              </div>
            </div>
            <button
              v-else
              type="button"
              class="mall-head__phone-icon"
              aria-label="내정보"
              @click="openLogin"
            >
              <AppIcon icon="mdi-account-outline" :size="22" />
            </button>
          </div>
        </div>

        <form
          ref="searchRef"
          class="mall-head__search"
          role="search"
          @submit.prevent="submitSearch"
        >
          <AppIcon class="mall-head__search-icon" icon="mdi-magnify" :size="22" />
          <input
            v-model="keyword"
            type="search"
            class="mall-head__search-input"
            placeholder="검색어"
            aria-label="상품검색"
            aria-autocomplete="list"
            :aria-expanded="suggestOpen"
            enterkeyhint="search"
            autocomplete="off"
            @focus="openSuggest"
            @input="openSuggest"
          />
          <button
            v-if="keyword"
            type="button"
            class="mall-head__search-clear"
            aria-label="검색어 지우기"
            @click="clearSearch"
          >
            <AppIcon icon="mdi-close-circle" :size="18" />
          </button>
          <button type="submit" class="mall-head__search-submit">검색</button>

          <div
            v-if="suggestOpen && (suggestProducts.length || recentSearches.length)"
            class="mall-head__suggest"
            role="listbox"
          >
            <div v-if="suggestProducts.length" class="mall-head__suggest-block">
              <p class="mall-head__suggest-label">추천상품</p>
              <button
                v-for="product in suggestProducts"
                :key="product.id"
                type="button"
                class="mall-head__suggest-item"
                role="option"
                @click="goProduct(product.id)"
              >
                <img v-if="product.image" :src="product.image" :alt="''" />
                <span>
                  <strong>{{ product.name }}</strong>
                  <em>{{ product.mallKind === 'SALE' ? `1개당 ${formatPrice(product.price)}원` : `${formatPrice(product.price)}원` }}</em>
                </span>
              </button>
            </div>
            <div v-if="recentSearches.length" class="mall-head__suggest-block">
              <div class="mall-head__suggest-head">
                <p class="mall-head__suggest-label">최근 검색어</p>
                <button type="button" class="mall-head__suggest-clear" @click="clearSearches">
                  전체삭제
                </button>
              </div>
              <div class="mall-head__suggest-chips">
                <button
                  v-for="item in recentSearches"
                  :key="item"
                  type="button"
                  class="mall-head__suggest-chip"
                  @click="goSearch(item)"
                >
                  {{ item }}
                </button>
              </div>
            </div>
          </div>
        </form>

        <nav class="mall-head__actions" aria-label="쇼핑 메뉴">
          <router-link :to="MALL_WISHLIST_PATH" class="mall-head__action">
            <span class="mall-head__action-icon">
              <AppIcon icon="mdi-heart-outline" :size="22" />
              <span v-if="wishCount" class="mall-head__badge">{{ badgeText(wishCount) }}</span>
            </span>
            <span class="mall-head__action-label">찜하기</span>
          </router-link>
          <router-link :to="MALL_MYPAGE_PATH" class="mall-head__action">
            <span class="mall-head__action-icon">
              <AppIcon icon="mdi-account-outline" :size="22" />
            </span>
            <span class="mall-head__action-label">마이페이지</span>
          </router-link>
          <router-link :to="MALL_CART_PATH" class="mall-head__action">
            <span class="mall-head__action-icon">
              <AppIcon icon="mdi-cart-outline" :size="22" />
              <span v-if="productCount" class="mall-head__badge">{{ badgeText(productCount) }}</span>
            </span>
            <span class="mall-head__action-label">장바구니</span>
          </router-link>
        </nav>
      </div>
    </div>

    <nav
      v-if="showCategories"
      class="mall-head__cats"
      :class="{ 'is-home': isMallHome }"
      aria-label="상품 카테고리"
    >
      <div class="mall-head__cats-inner">
        <router-link
          v-for="cat in categories"
          :key="cat.id"
          :to="categoryTo(cat.id)"
          custom
          v-slot="{ href, navigate }"
        >
          <a
            :href="href"
            class="mall-head__cat"
            :class="{ 'is-active': isCategoryActive(cat.id) }"
            :aria-current="isCategoryActive(cat.id) ? 'page' : undefined"
            @click="navigate"
          >
            <AppIcon :icon="cat.icon" :size="16" />
            <span>{{ cat.label }}</span>
          </a>
        </router-link>
      </div>
    </nav>
  </header>

  <MallLoginModal
    :open="loginOpen"
    :redirect-to="route.fullPath"
    @close="closeLogin"
    @success="onLoginSuccess"
  />
</template>

<script setup lang="ts">
import { computed, nextTick, onMounted, onUnmounted, ref, watch } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import AppIcon from '@/components/common/AppIcon.vue'
import MallLoginModal from '@/components/mall/MallLoginModal.vue'
import { ensureLiveProducts } from '@/api/mall'
import { fetchMallNotifies, markMallNotifiesRead, type MallNotifyItem } from '@/api/mallNotify'
import { useCart } from '@/composables/useCart'
import { useAdminAuth } from '@/composables/useAdminAuth'
import { useMemberAuth } from '@/composables/useMemberAuth'
import { useRecentSearches } from '@/composables/useRecentSearches'
import { useWishlist } from '@/composables/useWishlist'
import { useSellerAuth } from '@/composables/useSellerAuth'
import { useSellerCenter } from '@/composables/useSellerCenter'
import { staffManagePath } from '@/composables/useUnifiedLogin'
import { clearMallHeaderOffset, publishMallHeaderOffset } from '@/composables/useMallHeaderOffset'
import {
  MALL_CART_PATH,
  MALL_HOME_PATH,
  MALL_MYPAGE_PATH,
  MALL_NAME,
  MALL_ORDERS_PATH,
  MALL_SHIPPING_PATH,
  MALL_WISHLIST_PATH,
  mallProductPath,
} from '@/constants/mall'
import { MALL_CATEGORY_META, formatPrice, getAllProducts } from '@/constants/mallProducts'

const route = useRoute()
const showCategories = computed(() => route.name !== 'mall-signup')
const isMallHome = computed(() => route.path === MALL_HOME_PATH)
const router = useRouter()
const { productCount } = useCart()
const { wishCount } = useWishlist()
const { recentSearches, pushSearch, clearSearches } = useRecentSearches()
const { isLoggedIn, member, logout } = useMemberAuth()
const { admin, isAdminLoggedIn, initAdminAuth, logout: logoutAdmin } = useAdminAuth()
const { seller, canOpen: canOpenSellerCenter, refreshSellerAuth } = useSellerCenter()
const { logout: logoutSeller } = useSellerAuth()
const managePageTo = computed(() =>
  staffManagePath({
    isAdmin: isAdminLoggedIn.value,
    isSeller: canOpenSellerCenter.value,
    seller: seller.value,
  }),
)

const keyword = ref(typeof route.query.q === 'string' ? route.query.q : '')
const stuck = ref(false)
const loginOpen = ref(false)
const loginButtonRef = ref<HTMLButtonElement | null>(null)
const accountOpen = ref(false)
const accountRef = ref<HTMLElement | null>(null)
const phoneAccountRef = ref<HTMLElement | null>(null)
const notifyOpen = ref(false)
const notifyRef = ref<HTMLElement | null>(null)
const notifies = ref<MallNotifyItem[]>([])
const unreadCount = ref(0)
const searchRef = ref<HTMLElement | null>(null)
const suggestOpen = ref(false)
const categories = MALL_CATEGORY_META

const suggestProducts = computed(() => {
  const q = keyword.value.trim().toLowerCase()
  const all = getAllProducts()
  if (!q) return all.slice(0, 5)
  return all
    .filter((product) => product.name.toLowerCase().includes(q))
    .slice(0, 6)
})

function openSuggest() {
  suggestOpen.value = true
}

function closeSuggest() {
  suggestOpen.value = false
}

function goSearch(q: string) {
  keyword.value = q
  pushSearch(q)
  closeSuggest()
  router.push({ path: MALL_HOME_PATH, query: { q } })
}

function goProduct(id: string) {
  closeSuggest()
  router.push(mallProductPath(id))
}

function categoryTo(id: string) {
  if (id === '전체') return { path: MALL_HOME_PATH, query: {} }
  return { path: MALL_HOME_PATH, query: { cat: id } }
}

function isCategoryActive(id: string) {
  if (route.path !== MALL_HOME_PATH) return false
  const current = typeof route.query.cat === 'string' ? route.query.cat : ''
  if (id === '전체') return !current
  return current === id
}

const personName = computed(
  () => member.value?.bossName?.trim() || member.value?.userId || '회원',
)
const welcomeName = computed(
  () => member.value?.orgName?.trim() || personName.value,
)
const phoneAccountName = computed(() => {
  if (isLoggedIn.value) return welcomeName.value
  if (isAdminLoggedIn.value) return admin.value?.name?.trim() || admin.value?.userId || '관리자'
  if (canOpenSellerCenter.value) {
    return (
      seller.value?.storeName?.trim()
      || seller.value?.ownerName?.trim()
      || seller.value?.sellerId
      || '판매업체'
    )
  }
  return ''
})
const phoneAccountTitle = computed(() => (isLoggedIn.value ? personName.value : phoneAccountName.value))
const myInfoActive = computed(
  () => route.path.startsWith(MALL_MYPAGE_PATH) && !route.path.startsWith(MALL_WISHLIST_PATH),
)

watch(
  () => route.query.q,
  (q) => {
    keyword.value = typeof q === 'string' ? q : ''
  },
)

watch(
  () => route.fullPath,
  () => {
    loginOpen.value = false
    accountOpen.value = false
    notifyOpen.value = false
  },
)

watch(isLoggedIn, (loggedIn) => {
  if (loggedIn) void loadNotifies()
  else {
    notifies.value = []
    unreadCount.value = 0
  }
})

const badgeText = (count: number) => (count > 99 ? '99+' : String(count))

function submitSearch() {
  const q = keyword.value.trim()
  if (q) pushSearch(q)
  closeSuggest()
  router.push({ path: MALL_HOME_PATH, query: q ? { q } : {} })
}

function clearSearch() {
  keyword.value = ''
  if (route.path === MALL_HOME_PATH && route.query.q) {
    router.push({ path: MALL_HOME_PATH })
  }
}

async function loadNotifies() {
  if (!isLoggedIn.value) return
  try {
    const result = await fetchMallNotifies()
    notifies.value = result.items
    unreadCount.value = result.unread
  } catch {
    notifies.value = []
    unreadCount.value = 0
  }
}

function toggleNotify() {
  notifyOpen.value = !notifyOpen.value
  accountOpen.value = false
  if (notifyOpen.value) void loadNotifies()
}

async function openNotify(item: MallNotifyItem) {
  notifyOpen.value = false
  try {
    unreadCount.value = await markMallNotifiesRead(item.idx)
    const found = notifies.value.find((row) => row.idx === item.idx)
    if (found) found.read = true
  } catch {
    /* 읽음 실패해도 화면은 연다 */
  }
  /* 문의 답변 알림은 orderCode 자리에 상품 코드가 들어 있다 */
  if (item.type === 'QNA_ANSWER' && item.orderCode) {
    await router.push(`/mall/products/${encodeURIComponent(item.orderCode)}`)
    return
  }
  if (item.orderCode) {
    await router.push(`/mall/orders/${encodeURIComponent(item.orderCode)}`)
    return
  }
  await router.push(MALL_ORDERS_PATH)
}

let accountCloseTimer = 0

function openAccount() {
  window.clearTimeout(accountCloseTimer)
  accountOpen.value = true
  notifyOpen.value = false
}

function closeAccountSoon() {
  window.clearTimeout(accountCloseTimer)
  accountCloseTimer = window.setTimeout(() => {
    accountOpen.value = false
  }, 160)
}

function toggleAccount() {
  window.clearTimeout(accountCloseTimer)
  accountOpen.value = !accountOpen.value
  notifyOpen.value = false
}

function closeAccount() {
  window.clearTimeout(accountCloseTimer)
  accountOpen.value = false
}

function openLogin() {
  accountOpen.value = false
  notifyOpen.value = false
  suggestOpen.value = false
  loginOpen.value = true
}

function closeLogin() {
  loginOpen.value = false
  void nextTick(() => loginButtonRef.value?.focus())
}

async function onLoginSuccess() {
  loginOpen.value = false
}

async function leaveMall() {
  await router.push('/')
}

async function onStaffLogout() {
  closeAccount()
  if (isAdminLoggedIn.value) await logoutAdmin()
  if (canOpenSellerCenter.value) await logoutSeller()
  await leaveMall()
}

async function onLogout() {
  closeAccount()
  await logout()
  await leaveMall()
}

const onScroll = () => {
  stuck.value = window.scrollY > 8
  syncHeaderOffset()
}

const onDocClick = (event: MouseEvent) => {
  const target = event.target as Node
  const inAccount = Boolean(
    (accountRef.value && accountRef.value.contains(target))
    || (phoneAccountRef.value && phoneAccountRef.value.contains(target)),
  )
  if (accountOpen.value && !inAccount) {
    accountOpen.value = false
  }
  if (notifyOpen.value && notifyRef.value && !notifyRef.value.contains(target)) {
    notifyOpen.value = false
  }
  if (suggestOpen.value && searchRef.value && !searchRef.value.contains(target)) {
    suggestOpen.value = false
  }
}

const onKeydown = (event: KeyboardEvent) => {
  if (event.key === 'Escape') {
    accountOpen.value = false
    notifyOpen.value = false
    suggestOpen.value = false
  }
}

let offsetRaf = 0
let offsetObserver: ResizeObserver | null = null
const syncHeaderOffset = () => {
  if (offsetRaf) return
  offsetRaf = window.requestAnimationFrame(() => {
    offsetRaf = 0
    publishMallHeaderOffset()
  })
}

onMounted(() => {
  onScroll()
  void initAdminAuth()
  refreshSellerAuth()
  ensureLiveProducts()
  void loadNotifies()
  syncHeaderOffset()
  const head = document.querySelector('.mall-head')
  if (head && typeof ResizeObserver !== 'undefined') {
    offsetObserver = new ResizeObserver(() => syncHeaderOffset())
    offsetObserver.observe(head)
  }
  window.addEventListener('scroll', onScroll, { passive: true })
  window.addEventListener('resize', syncHeaderOffset, { passive: true })
  document.addEventListener('click', onDocClick)
  window.addEventListener('keydown', onKeydown)
})

onUnmounted(() => {
  window.removeEventListener('scroll', onScroll)
  window.removeEventListener('resize', syncHeaderOffset)
  document.removeEventListener('click', onDocClick)
  window.removeEventListener('keydown', onKeydown)
  offsetObserver?.disconnect()
  offsetObserver = null
  if (offsetRaf) window.cancelAnimationFrame(offsetRaf)
  window.clearTimeout(accountCloseTimer)
  clearMallHeaderOffset()
})
</script>

<style lang="scss" src="./MallHeader.scss"></style>
